import React from 'react'
import { Stack, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CORAL_600, GRAY_600 } from 'constants/styling/theme'
import { AtticScope, CellarScope } from 'models/purchaseFlow/measurementDTO'
import { MeasurementQuoteLineItemType } from 'models/purchaseFlow/measurementQuoteDTO'
import type { MeasurementQuoteLineItemDTO } from 'models/purchaseFlow/measurementQuoteDTO'
import type { CartItemId } from 'models/purchaseFlowRevamp'
import { bigFromFee } from 'utils/price/decimals'
import { formatPrice } from 'utils/price'
import { usePfMeasurementQuote } from 'dataQueries/purchaseFlowRevampMeasurement.query'

import { usePfStore } from '../../store'
import { makeConfigKey } from '../../store/slices/productConfigSlice'
import {
  MEASUREMENT_DEFAULT,
  isUnitBasedPropertyType,
  toMeasurementRequest
} from '../../modules/measurement/measurement.dto'
import type { MeasurementDraftDTO } from '../../modules/measurement/measurement.dto'

/**
 * What the measurement line is made of, under the line itself.
 *
 * The cart row can only show one number, and for this product that number is the
 * sum of carriers the engine derived — base, units, floors, cellar, area
 * surcharge, add-ons. Without the breakdown a client sees a price with no visible
 * cause and no way to tell which answer moved it.
 *
 * Reads the same query the configuration step does, so it is served from cache
 * rather than fetched twice. Mounted only for a line that HAS a measurement
 * configuration — every other row would otherwise pay for a hook it never uses.
 */
export const MeasurementBreakdown: React.FC<{ cartItemId: CartItemId }> = ({ cartItemId }) => {
  const { t } = useTranslation(['purchase_flow'], { keyPrefix: 'measurement_step' })

  const stored = usePfStore(
    (s) => s.productConfigs[makeConfigKey(cartItemId, 'measurement')] as Partial<MeasurementDraftDTO> | undefined
  )
  const draft: MeasurementDraftDTO = { ...MEASUREMENT_DEFAULT, ...stored }
  const request = toMeasurementRequest(draft)

  const { data, isError } = usePfMeasurementQuote(request as never)
  const quote = data?.data

  /**
   * The engine names a carrier; the client needs to read it against the answer
   * they gave. A free cellar is the case that matters — billed at zero, it must
   * still say which scope earned that, or a 0,00 line looks like a fault.
   */
  const label = (item: MeasurementQuoteLineItemDTO): string => {
    if (item.type === MeasurementQuoteLineItemType.CATEGORY_BASE) {
      // A unit-based category calls its base line "first residential unit"; keyed
      // on how the engine actually priced it, not on the category alone.
      if (isUnitBasedPropertyType(draft.propertyType)) return t('cost_assumption.line.FIRST_RESIDENTIAL_UNIT')
      const propertyType = t(`property_type_card.${draft.propertyType}`)
      return item.quantity
        ? t('cost_assumption.line.CATEGORY_BASE_FLOORS', { propertyType, count: item.quantity })
        : t('cost_assumption.line.CATEGORY_BASE', { propertyType })
    }
    if (item.type === MeasurementQuoteLineItemType.RESIDENTIAL_UNIT) {
      return t('cost_assumption.line.RESIDENTIAL_UNIT', { count: item.quantity ?? 1 })
    }
    if (item.type === MeasurementQuoteLineItemType.ADDITIONAL_FLOOR) {
      return t('cost_assumption.line.ADDITIONAL_FLOOR', { quantity: item.quantity ?? 1 })
    }
    if (item.type === MeasurementQuoteLineItemType.CELLAR && draft.cellarScope === CellarScope.ROOM) {
      return t('cost_assumption.line.CELLAR_ROOM')
    }
    if (item.type === MeasurementQuoteLineItemType.CELLAR && draft.cellarScope === CellarScope.PRESENT_NOT_MEASURED) {
      return t('cost_assumption.line.CELLAR_PRESENT_NOT_MEASURED')
    }
    if (item.type === MeasurementQuoteLineItemType.ATTIC && draft.atticScope === AtticScope.PRESENT_NOT_MEASURED) {
      return t('cost_assumption.line.ATTIC_PRESENT_NOT_MEASURED')
    }
    return t(`cost_assumption.line.${item.type}`)
  }

  return (
    <Stack gap={0.2} sx={{ mt: 0.2 }} data-cy="pf-revamp-cart-item-breakdown">

      {/* A failed quote must not leave the last amounts standing as if current. */}
      {isError &&
        <Typography variant="text-xs" color={CORAL_600}>{t('cost_assumption.error')}</Typography>
      }

      {(quote?.lineItems ?? []).map((item) => (
        <Stack key={item.type} direction="row" justifyContent="space-between" gap={0.8}>
          <Stack direction="row" gap={0.4}>
            <Typography variant="text-xs" color={GRAY_600}>+</Typography>
            <Typography variant="text-xs" color={GRAY_600}>{label(item)}</Typography>
          </Stack>
          <Typography variant="text-xs" color={GRAY_600} whiteSpace="nowrap">
            {formatPrice(bigFromFee(item.amount), quote?.currency)}
          </Typography>
        </Stack>
      ))}

    </Stack>
  )
}
